import clsx from "clsx";
import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { Accordion } from "@/apps/partner/components/Accordion/Accordion";
import { AddDeviceFormType } from "@/apps/partner/types";
import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { RadioButton } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { Step } from "@/components/Onboarding/Steps/Step";
import { useCheckMsisdnAvailability, useGetOnpProviders } from "@/services/api";
import { Provider } from "@/services/model";
import { PHONE_REGEX } from "@/utils/constants";
// mnpContractType: id 1 = postpaid | id 2 = prepaid
const mnpContractType = {
  postpaid: { id: 1, description: "postpaid" },
  prepaid: { id: 2, description: "prepaid" },
};

// id 2 = after the contract ends | id 3 = premature contract termination
const mnpTime = {
  after: { id: 2, description: "after" },
  before: { id: 3, description: "before" },
};

const numberPrefix = "+41 (0) ";

export const formatNumber = (input: string, oldInput = "") => {
  if (input.indexOf(numberPrefix) !== 0) {
    return oldInput;
  } else {
    const match = input.match(PHONE_REGEX);
    const formatted = match
      ? match
          .slice(1)
          .filter((x) => x)
          .join(" ")
      : input;

    return formatted.substring(0, 32);
  }
};

const providersToObj = (arr: Provider[]): Record<string, number> =>
  Object.fromEntries(
    arr
      .filter(({ providerName, id }) => providerName && id)
      .map(({ providerName, id }) => [providerName, id]),
  );

// Format MSISDN for API: strip +41 prefix and return 10 digits starting with 0 (no spaces)
const formatMsisdnForApi = (input: string): string => {
  const withoutIntl = input.replace(/^(\+41\s*\(0\)\s*|\+41\s*)/, "");
  const digits = withoutIntl.replace(/\D/g, "");
  const msisdn = digits.startsWith("0") ? digits : `0${digits}`;

  return msisdn.slice(0, 10);
};

export const NumberPorting: FC = () => {
  const { t } = useTranslation();

  const { data, isFetching: isLoadingProviders } = useGetOnpProviders();
  const providers = useMemo(() => data && providersToObj(data), [data]);
  const [msisdnValidationStatus, setMsisdnValidationStatus] = useState<
    "idle" | "loading" | "valid" | "invalid"
  >("idle");
  const [msisdnError, setMsisdnError] = useState<string>("");

  // MSISDN validation
  const { mutate: checkMsisdnAvailability, isLoading: isCheckingMsisdn } =
    useCheckMsisdnAvailability({
      mutation: {
        onSuccess: (data) => {
          if (data.msisdnStatus === "ALREADY_IN_USE") {
            setMsisdnValidationStatus("invalid");
            setMsisdnError(
              t("Onboarding.sections.number-porting.msisdnAlreadyInUse"),
            );
          } else if (data.msisdnStatus === "SUSPENDED") {
            setMsisdnValidationStatus("invalid");
            setMsisdnError("This number is suspended and cannot be ported");
          } else if (data.msisdnStatus === "AVAILABLE") {
            setMsisdnValidationStatus("valid");
            setMsisdnError("");
          } else {
            setMsisdnValidationStatus("invalid");
            setMsisdnError(t("Common.generalForm.error.genericError"));
          }
        },
        onError: (error: any) => {
          setMsisdnValidationStatus("invalid");

          const errorStatus = error?.response?.status;
          const errorData = error?.response?.data;

          // Check for 400 Bad Request
          if (errorStatus === 400) {
            // The error data is a string that contains the JSON response
            // Format: "400 Bad Request: \"{\"code\":400,\"msisdnStatus\":\"ALREADY_IN_USE\",\"message\":\"SIM ist bereits aktiv ausgespielt\"}\""

            if (typeof errorData === "string") {
              try {
                // Extract the JSON part from the string
                const jsonMatch = errorData.match(/\{.*\}/);
                if (jsonMatch) {
                  const parsedData = JSON.parse(jsonMatch[0]);

                  if (parsedData.msisdnStatus === "ALREADY_IN_USE") {
                    setMsisdnError(
                      t(
                        "Onboarding.sections.number-porting.msisdnAlreadyInUse",
                      ),
                    );

                    return;
                  }
                }
              } catch (parseError) {
                // Silent fallback to generic error
              }
            }

            // Fallback: check if the error message contains the specific text
            if (
              typeof errorData === "string" &&
              errorData.includes("SIM ist bereits aktiv ausgespielt")
            ) {
              setMsisdnError(
                t("Onboarding.sections.number-porting.msisdnAlreadyInUse"),
              );

              return;
            }
          }

          setMsisdnError(t("Common.generalForm.error.genericError"));
        },
      },
    });

  // Use the unified form context instead of local form state
  const {
    register,
    watch,
    control,
    trigger,
    setValue,
    unregister,
    formState: { errors },
  } = useFormContext<AddDeviceFormType>();

  const oldNumber = watch("mnpData.portedPhoneNumber.phone");
  const formValues = watch();

  // Unregister mnpTime field when prepaid is selected
  useEffect(() => {
    if (
      formValues?.mnpData?.mnpContractType?.id?.toString() ===
      mnpContractType.prepaid.id?.toString()
    ) {
      unregister("mnpData.mnpTime");
    }
  }, [formValues?.mnpData?.mnpContractType?.id, unregister]);

  const terminationIsDisabled = !(
    formValues?.mnpData?.mnpContractType?.id?.toString() ===
    mnpContractType.postpaid.id?.toString()
  );

  const terminationBeforeContractEnd =
    formValues?.mnpData?.mnpTime?.id?.toString() ===
    mnpTime.before.id?.toString();

  return (
    <Step id="partner-number-porting" data-testid="onboarding-number-porting">
      <Accordion label={""} canOpen={false} border={false}>
        <div className="content-container">
          <div className="form-field-wrapper relative mt-4">
            <FormField<AddDeviceFormType>
              id="telephone-number-input"
              autocomplete="tel"
              name="mnpData.portedPhoneNumber.phone"
              label={`1. ${t(
                "Onboarding.sections.number-porting.currentNumber",
              )}`}
              errors={errors}
              register={register}
              type="text"
              placeholder={t(
                "Onboarding.sections.number-porting.placeholder.currentNumber",
              )}
              onFocus={() => {
                if (!watch("mnpData.portedPhoneNumber.phone")) {
                  setValue("mnpData.portedPhoneNumber.phone", numberPrefix);
                }
              }}
              options={{
                required: t("translation:label.validation.required"),
                pattern: {
                  value: PHONE_REGEX,
                  message: t("Onboarding.sections.number-porting.numberFormat"),
                },
                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                  const input = e.target as HTMLInputElement;
                  const value = formatNumber(input.value, oldNumber);
                  setValue("mnpData.portedPhoneNumber.phone", value);
                  // Trigger validation after setting the value
                  trigger("mnpData.portedPhoneNumber.phone");
                },
                onBlur: () => {
                  // Trigger validation when user leaves the field
                  const currentValue = watch("mnpData.portedPhoneNumber.phone");
                  if (
                    currentValue &&
                    PHONE_REGEX.test(currentValue) &&
                    msisdnValidationStatus !== "loading"
                  ) {
                    setMsisdnValidationStatus("loading");
                    const formattedMsisdn = formatMsisdnForApi(currentValue);
                    checkMsisdnAvailability({ msisdn: formattedMsisdn });
                  }
                },
              }}
            />

            <div
              className={`prefix-coloring ${
                watch("mnpData.portedPhoneNumber.phone") ? "" : "hidden"
              }`}
            />
            {/* MSISDN validation error */}
            {msisdnError && (
              <ErrorMessage
                message={msisdnError}
                name="mnpData.portedPhoneNumber.phone"
              />
            )}
          </div>

          <div className="form-field-container form-field-wrapper mt-6">
            {providers && (
              <Controller<AddDeviceFormType>
                control={control}
                name="mnpData.currentProvider.description"
                rules={{
                  required: t("translation:label.validation.required"),
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Combobox
                      label={`2. ${t(
                        "Onboarding.sections.number-porting.currentProvider",
                      )}`}
                      field={{
                        ...field,
                        onChange: (value: any) => {
                          // Set the provider ID
                          field.onChange(value);
                          // Also set the provider description/name
                          if (value && providers[value]) {
                            setValue(
                              "mnpData.currentProvider.id",
                              providers[value],
                            );
                          }
                        },
                      }}
                      dataTestId="currentProviderCombobox"
                      options={Object.keys(providers)}
                      placeholder={t(
                        "Onboarding.sections.number-porting.placeholder.currentProvider",
                      )}
                      hasError={!!error}
                    />
                    {error && (
                      <div className="mt-1 text-sm text-secondary-100">
                        {error.message}
                      </div>
                    )}
                  </>
                )}
              />
            )}
          </div>
        </div>

        <div className="content-container">
          <fieldset
            className="form-field-wrapper mt-7"
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              setValue(
                "mnpData.mnpContractType.description",
                value === "1" ? "postpaid" : "prepaid",
              );
              trigger("mnpData.mnpContractType.id");

              // If prepaid is selected, unregister mnpTime completely
              if (value === mnpContractType.prepaid.id.toString()) {
                setValue("mnpData.mnpTime.id", 0);
              }
            }}
          >
            <legend className="input-label">
              {`3. ${t(
                "Onboarding.sections.number-porting.currentContractType",
              )}`}
            </legend>
            <div
              className={clsx(
                errors.mnpData?.mnpContractType?.id && "with-errors",
              )}
            >
              <RadioButton
                name="mnpData.mnpContractType.id"
                value={mnpContractType.prepaid.id}
                errors={errors}
                register={register}
                label={t("Onboarding.sections.number-porting.prepaid")}
                options={{
                  required: t("translation:label.validation.required"),
                }}
                className="mb-5"
              />

              <RadioButton
                name="mnpData.mnpContractType.id"
                value={mnpContractType.postpaid.id}
                errors={errors}
                register={register}
                label={t("Onboarding.sections.number-porting.postpaid")}
                options={{
                  required: t("translation:label.validation.required"),
                }}
              />

              {errors.mnpData?.mnpContractType?.id && (
                <div className="ml-7">
                  <ErrorMessage
                    message={t("translation:label.validation.required")}
                    name="mnpData.mnpContractType.id"
                  />
                </div>
              )}
            </div>
          </fieldset>

          <fieldset
            className={clsx(
              "form-field-wrapper mt-6",
              terminationIsDisabled && "disabled",
            )}
            data-testid="termination"
            onChange={(e) => {
              const value = (e.target as HTMLInputElement).value;
              setValue(
                "mnpData.mnpTime.description",
                value === "2" ? "after" : "before",
              );
              trigger("mnpData.mnpTime.id");
            }}
          >
            <legend className="input-label">
              {`4. ${t("Onboarding.sections.number-porting.termination")}`}
            </legend>

            <p className="mb-2 text-sm">
              {t("Onboarding.sections.number-porting.steps")}
            </p>

            <div className={clsx(errors.mnpData?.mnpTime?.id && "with-errors")}>
              <RadioButton
                name="mnpData.mnpTime.id"
                value={mnpTime.after.id}
                errors={errors}
                register={register}
                label={t(
                  "Onboarding.sections.number-porting.afterContractEnds",
                )}
                labelComponent={
                  <Trans
                    components={[
                      <span
                        key="recommended"
                        className="font-x-small m-2 text-primary-75"
                      />,
                    ]}
                    i18nKey="Onboarding.sections.number-porting.recommended"
                  />
                }
                options={{
                  required: t("translation:label.validation.required"),
                  disabled: terminationIsDisabled,
                }}
                className="mb-5"
              />

              <RadioButton
                name="mnpData.mnpTime.id"
                value={mnpTime.before.id}
                errors={errors}
                register={register}
                label={t(
                  "Onboarding.sections.number-porting.beforeContractEnds",
                )}
                options={{
                  required: t("translation:label.validation.required"),
                  disabled: terminationIsDisabled,
                }}
              />
            </div>

            {errors.mnpData?.mnpTime?.id && (
              <div className="ml-7">
                <ErrorMessage
                  message={t("translation:label.validation.required")}
                  name="mnpData.mnpTime.id"
                />
              </div>
            )}

            {terminationBeforeContractEnd && (
              <div className="ml-7">
                <ErrorMessage
                  message={t(
                    "Onboarding.sections.number-porting.cost-warning",
                  ).toString()}
                  name={"mnpTime"}
                />
              </div>
            )}
          </fieldset>
        </div>
      </Accordion>
    </Step>
  );
};
